import React from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Paper,
  Title,
  Divider,
  Grid,
  Card,
  Alert
} from '@mantine/core';
import {
  IconUser,
  IconPhone,
  IconCreditCard,
  IconSchool,
  IconCalendar,
  IconNote,
  IconBell,
  IconBellOff,
  IconAlertCircle
} from '@tabler/icons-react';

const MemberDetails = ({ member }) => {
  const InfoCard = ({ icon: Icon, title, value, color = 'blue' }) => (
    <Card withBorder p="md">
      <Group>
        <Icon size={24} color={`var(--mantine-color-${color}-6)`} />
        <Stack gap="xs">
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text size="sm" fw={600}>
            {value}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  const getPaymentBadge = () => {
    return member.payment_method === 'Razorpay' 
      ? <Badge color="green" size="lg">Razorpay Payment</Badge>
      : <Badge color="blue" size="lg">Manual Entry</Badge>;
  };

  const getBatchInfo = () => {
    if (!member.batch) {
      return {
        badge: <Badge color="gray" size="lg">No Batch Assigned</Badge>,
        details: 'Member is not assigned to any batch'
      };
    }
    
    return {
      badge: <Badge color="purple" size="lg">Batch #{member.batch.batch_number}</Badge>,
      details: `Curriculum: ${member.batch.curriculum?.title || 'Not Set'}`
    };
  };

  const batchInfo = getBatchInfo();

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>{member.full_name}</Title>
        <Group>
          {getPaymentBadge()}
          {member.has_disabled_wa_notifications ? (
            <Badge color="red" leftSection={<IconBellOff size={12} />}>
              WA Disabled
            </Badge>
          ) : (
            <Badge color="green" leftSection={<IconBell size={12} />}>
              WA Enabled
            </Badge>
          )}
        </Group>
      </Group>

      <Divider label="Contact Information" labelPosition="center" />

      <Grid>
        <Grid.Col span={6}>
          <InfoCard
            icon={IconUser}
            title="Full Name"
            value={member.full_name}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <InfoCard
            icon={IconPhone}
            title="Phone Number"
            value={`${member.phone_number_country_code} ${member.phone_number}`}
            color="green"
          />
        </Grid.Col>
      </Grid>

      <Divider label="Payment Information" labelPosition="center" />

      <Grid>
        <Grid.Col span={6}>
          <Paper withBorder p="md">
            <Group>
              <IconCreditCard size={24} color="var(--mantine-color-green-6)" />
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Payment Method</Text>
                <Text size="sm" fw={600}>{member.payment_method}</Text>
              </Stack>
            </Group>
          </Paper>
        </Grid.Col>
        <Grid.Col span={6}>
          <Paper withBorder p="md">
            <Group>
              <IconCreditCard size={24} color="var(--mantine-color-blue-6)" />
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Payment ID</Text>
                <Text size="sm" fw={600}>
                  {member.razorpay_payment_id || 'N/A'}
                </Text>
              </Stack>
            </Group>
          </Paper>
        </Grid.Col>
      </Grid>

      <Divider label="Batch Assignment" labelPosition="center" />

      <Paper withBorder p="md">
        <Group justify="space-between" mb="sm">
          <Group>
            <IconSchool size={24} color="var(--mantine-color-purple-6)" />
            <Text fw={600}>Batch Information</Text>
          </Group>
          {batchInfo.badge}
        </Group>
        <Text size="sm" c="dimmed">
          {batchInfo.details}
        </Text>
        {member.batch && (
          <Stack gap="xs" mt="sm">
            <Text size="xs" c="dimmed">
              Start Time: {new Date(member.batch.batch_start_time).toLocaleString('en-IN')}
            </Text>
            <Text size="xs" c="dimmed">
              Cap Time: {new Date(member.batch.cap_time).toLocaleString('en-IN')}
            </Text>
          </Stack>
        )}
      </Paper>

      <Divider label="Timeline" labelPosition="center" />

      <Paper withBorder p="md">
        <Group>
          <IconCalendar size={24} color="var(--mantine-color-orange-6)" />
          <Stack gap="xs">
            <Text size="sm" c="dimmed" fw={500}>Member Since</Text>
            <Text size="sm" fw={600}>
              {new Date(member.member_created_datetime).toLocaleString('en-IN', {
                dateStyle: 'full',
                timeStyle: 'short'
              })}
            </Text>
          </Stack>
        </Group>
      </Paper>

      {member.admin_note && (
        <>
          <Divider label="Admin Notes" labelPosition="center" />
          <Alert icon={<IconNote size="1rem" />} color="blue">
            <Text size="sm">{member.admin_note}</Text>
          </Alert>
        </>
      )}

      <Divider label="System Information" labelPosition="center" />

      <Grid>
        <Grid.Col span={6}>
          <Paper withBorder p="sm">
            <Text size="xs" c="dimmed" fw={500}>Created At</Text>
            <Text size="xs">
              {new Date(member.created_at).toLocaleString('en-IN')}
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={6}>
          <Paper withBorder p="sm">
            <Text size="xs" c="dimmed" fw={500}>Last Updated</Text>
            <Text size="xs">
              {new Date(member.updated_at).toLocaleString('en-IN')}
            </Text>
          </Paper>
        </Grid.Col>
      </Grid>

      {member.progress && member.progress.length > 0 && (
        <>
          <Divider label="Course Progress" labelPosition="center" />
          <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
            <Text size="sm">
              Progress tracking will be displayed here once curriculum features are implemented.
            </Text>
          </Alert>
        </>
      )}
    </Stack>
  );
};

export default MemberDetails;